import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customRequests, setCustomRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchCustomRequests();
  }, [user]);

  const fetchCustomRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/custom-art`);
      setCustomRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch custom requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl mb-12"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="profile-title"
          >
            My Profile
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* User Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-lg border border-black/5"
            >
              <div className="flex items-center justify-center w-20 h-20 bg-[#F5F5F0] rounded-full mx-auto mb-6">
                <User size={32} strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {user?.full_name}
              </h2>
              <p className="text-[#888888] text-center text-sm mb-6">{user?.email}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/orders')}
                  data-testid="profile-orders-btn"
                  className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300"
                >
                  View Orders
                </button>
                <button
                  onClick={() => navigate('/orders')}
                  data-testid="profile-custom-art-btn"
                  className="w-full bg-transparent border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-full px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300"
                >
                  View Wishlist
                </button>
              </div>
            </motion.div>

            {/* Custom Art Requests */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 bg-white p-8 rounded-lg border border-black/5"
            >
              <h2 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Custom Art Requests
              </h2>

              {customRequests.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#888888] mb-4">No custom art requests yet</p>
                  <button
                    onClick={() => navigate('/custom-art')}
                    className="text-[#1A1A1A] hover:underline text-sm uppercase tracking-widest"
                  >
                    Create Your First Request
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {customRequests.map((request) => (
                    <div
                      key={request.id}
                      data-testid={`custom-request-${request.id}`}
                      className="p-6 border border-black/5 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm uppercase tracking-widest text-[#888888]">
                          Request #{request.id.slice(0, 8)}
                        </span>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs uppercase tracking-widest ${
                            request.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : request.status === 'in-progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {request.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#888888]">Size:</span>
                          <span className="ml-2 font-medium">{request.size}</span>
                        </div>
                        <div>
                          <span className="text-[#888888]">Theme:</span>
                          <span className="ml-2 font-medium">{request.theme}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-[#888888]">Color Palette:</span>
                          <span className="ml-2 font-medium">{request.color_palette}</span>
                        </div>
                        {request.additional_notes && (
                          <div className="col-span-2">
                            <span className="text-[#888888]">Notes:</span>
                            <p className="mt-2 text-[#4A4A4A]">{request.additional_notes}</p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-[#888888] mt-4">
                        Submitted {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
